"use client";

import Image from "next/image";
import { Origin } from "@/types/origin";
import { useOrigins } from "@/lib/react-query/queries/references/use-origins";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { Skeleton } from "../ui/skeleton";
import { useEffect, useMemo, useState } from "react";

// Some storage objects return 403 (ACL). Skip the Next optimizer so those
// failures stay in the browser instead of flooding the server log.
function OriginLogo({ origin }: { origin: Origin }) {
  if (!origin.logo) return null;

  return (
    <Image
      src={origin.logo}
      alt={origin.name}
      width={118}
      height={33}
      unoptimized
      className="object-contain w-22 h-8 md:w-29.5 md:h-8.25"
    />
  );
}

// Hiding a broken logo isn't enough: its carousel slide would still take up
// space (and its gap) in the track. Probe the logos first so the carousel is
// built from origins that actually render, and embla measures the final list
// only once. Returns null while the probe is still running.
function useOriginsWithLoadableLogo(origins: Origin[]) {
  const [loadable, setLoadable] = useState<Origin[] | null>(null);

  useEffect(() => {
    const withLogo = origins.filter((origin) => Boolean(origin.logo));

    if (withLogo.length === 0) {
      setLoadable([]);
      return;
    }

    let cancelled = false;

    Promise.all(
      withLogo.map(
        (origin) =>
          new Promise<Origin | null>((resolve) => {
            const img = new window.Image();
            img.onload = () => resolve(img.naturalWidth > 0 ? origin : null);
            img.onerror = () => resolve(null);
            img.src = origin.logo as string;
          }),
      ),
    ).then((results) => {
      if (cancelled) return;
      setLoadable(
        results.filter((origin): origin is Origin => origin !== null),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [origins]);

  return loadable;
}

// Loading skeleton for origins
export function OriginSectionSkeleton() {
  return (
    <section className="px-5 md:px-0 mb-10 md:mb-20">
      <div className="py-4 md:py-4 relative border border-gray-50 rounded-[10px] md:rounded-[24px] overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-linear-to-r from-white to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-white to-transparent z-10" />
        <div className="flex gap-6 justify-center">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              className="w-18 h-7 md:w-20.75 md:h-6.5 shrink-0"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

const OriginSection = () => {
  const { data: originsResponse, isLoading, error } = useOrigins();

  // Extract origins array from the API response
  const origins: Origin[] = useMemo(
    () => originsResponse?.data || [],
    [originsResponse],
  );

  // Only origins whose logo actually loads get a slide
  const visibleOrigins = useOriginsWithLoadableLogo(origins);

  // Memoize the auto-scroll plugin for better performance
  const autoScrollPlugin = useMemo(
    () =>
      AutoScroll({
        speed: 1,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        startDelay: 0,
        direction: "backward", // RTL direction
      }),
    [],
  );

  // Show skeleton while loading, or while the logos are being probed
  if (isLoading || (!error && origins.length > 0 && visibleOrigins === null)) {
    return <OriginSectionSkeleton />;
  }

  // Don't render anything if no origins, no loadable logo, or error
  if (error || !visibleOrigins || visibleOrigins.length === 0) {
    return null;
  }

  // Duplicate origins for seamless loop effect
  const duplicatedOrigins = [...visibleOrigins, ...visibleOrigins];

  return (
    <section className="px-5 md:px-0 mb-10 md:mb-20">
      <div className="py-4 md:py-4 relative border border-gray-50 rounded-[10px] md:rounded-[24px] overflow-hidden">
        {/* Faded edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-linear-to-r from-white to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-white to-transparent z-10" />

        <Carousel
          opts={{
            loop: true,
            align: "start",
            dragFree: true,
          }}
          plugins={[autoScrollPlugin]}
          dir="rtl"
          className="w-full"
        >
          <CarouselContent className="-mr-6">
            {duplicatedOrigins.map((origin, index) => (
              <CarouselItem
                key={`${origin.id}-${index}`}
                className="basis-auto pr-0 md:pr-6 flex items-center justify-center"
              >
                <OriginLogo origin={origin} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};

export default OriginSection;
